import React, { useCallback } from 'react';

import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useCreateParityNotification } from '~/hooks/apis/price-parity/useCreateParityNotification';
import { useGetUnsolvedParityNotifications } from '~/hooks/apis/price-parity/useGetUnsolvedParityNotifications';
import { useSendNotificationEmail } from '~/hooks/apis/price-parity/useSendNotificationEmail';
import { useVendorOfferSelection } from '~/hooks/apis/price-parity/useVendorOfferSelection';

import type { ParityCategory, ParityNotificationBody } from '~/types/services/parityNotifications';

import VendorOfferSelector from '../shared/VendorOfferSelector';
import { genSalesforceId } from '../shared/utils';

import ParityItemsTable from './ParityItemsTable';
import ParityNotificationsGrid from './ParityNotificationsGrid';
import type { FormValues } from './types';

const PARITY_CATEGORIES = [
  { value: 'no_base_rate', label: 'No Base Rate' },
  { value: 'merged_base_rate', label: 'Merged Base Rate' },
  { value: 'missing_base_rate', label: 'Missing Base Rate' },
];

export default function ParityNotifications() {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      opportunityId: '',
      vendorId: '',
      offerId: '',
      parityCategory: '',
      parityValue: '',
      parityUplift: '',
      ratePlanId: '',
      parityItems: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'parityItems' });
  const vendorIdValue = useWatch({ control, name: 'vendorId' });
  const vendorOfferSelection = useVendorOfferSelection(vendorIdValue);

  const {
    data: notifications = [],
    isLoading: notificationsLoading,
    error: notificationsError,
  } = useGetUnsolvedParityNotifications();

  const { sendEmailMutation, sendingEmailId } = useSendNotificationEmail();

  const createMutation = useCreateParityNotification(() => {
    reset();
    vendorOfferSelection.reset();
  });

  const onSubmit = (data: FormValues) => {
    const body: ParityNotificationBody = {
      opportunityId: data.opportunityId,
      vendorId: data.vendorId,
      offerId: data.offerId,
      parityCategory: data.parityCategory as ParityCategory,
    };
    if (data.parityValue) body.parityValue = data.parityValue;
    if (data.parityUplift) body.parityUplift = data.parityUplift;
    if (data.ratePlanId) body.ratePlanId = data.ratePlanId;
    if (data.parityItems.length > 0) body.parityItems = data.parityItems;
    createMutation.mutate(body);
  };

  const handleSendEmail = useCallback((id: string) => sendEmailMutation.mutate(id), [sendEmailMutation]);

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        Create Parity Notification (Staging Only)
      </Typography>

      <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={3}>
        {/* ── Vendor ID ── */}
        <Controller
          name="vendorId"
          control={control}
          rules={{ required: 'Required' }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Vendor ID"
              required
              disabled={isSubmitting}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              sx={{ maxWidth: 340 }}
              InputProps={{
                endAdornment: vendorOfferSelection.lookupLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
        />

        {/* ── Offer + Rate Plan selectors ── */}
        <VendorOfferSelector
          offers={vendorOfferSelection.offers}
          offersFetching={vendorOfferSelection.offersFetching}
          vendorEnabled={vendorOfferSelection.vendorEnabled}
          selectedOfferId={vendorOfferSelection.selectedOfferId}
          setSelectedOfferId={vendorOfferSelection.setSelectedOfferId}
          selectionRatePlans={vendorOfferSelection.selectionData?.ratePlans}
          selectionFetching={vendorOfferSelection.selectionFetching}
          setValue={setValue}
          showProperty={false}
          disabled={isSubmitting}
        />

        {/* ── Manual ID fields ── */}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Controller
            name="opportunityId"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Opportunity ID"
                required
                disabled={isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 280 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate a random UUID">
                        <Button
                          size="small"
                          variant="text"
                          sx={{ minWidth: 0, fontSize: 11 }}
                          onClick={() => setValue('opportunityId', genSalesforceId('006'))}
                          tabIndex={-1}
                        >
                          Gen
                        </Button>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <Controller
            name="offerId"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Offer ID"
                required
                disabled={isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 280 }}
              />
            )}
          />
          <Controller
            name="parityCategory"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                label="Parity Category"
                required
                disabled={isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 200 }}
              >
                {PARITY_CATEGORIES.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="parityValue"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Parity Value"
                placeholder="e.g. $1,234"
                disabled={isSubmitting}
                sx={{ minWidth: 160 }}
              />
            )}
          />
          <Controller
            name="parityUplift"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Parity Uplift"
                placeholder="e.g. 5.2%"
                disabled={isSubmitting}
                sx={{ minWidth: 160 }}
              />
            )}
          />
          <Controller
            name="ratePlanId"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Rate Plan ID" disabled={isSubmitting} sx={{ minWidth: 240 }} />
            )}
          />
        </Stack>

        <Divider />

        {/* ── Parity items (weekly suggestions) ── */}
        <ParityItemsTable
          control={control}
          isSubmitting={isSubmitting}
          fields={fields}
          append={append}
          remove={remove}
        />

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            variant="text"
            disabled={isSubmitting}
            onClick={() => {
              reset();
              vendorOfferSelection.reset();
            }}
          >
            Clear
          </Button>
          <Button variant="contained" type="submit" disabled={isSubmitting}>
            Create Notification
          </Button>
        </Stack>
      </Stack>

      {/* ── Recent notifications table ── */}
      <ParityNotificationsGrid
        notifications={notifications}
        isLoading={notificationsLoading}
        error={notificationsError}
        onSendEmail={handleSendEmail}
        sendingId={sendingEmailId}
      />
    </Box>
  );
}
